// src/frontend/components/OrderEditWorkspace.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { FaEdit, FaSave, FaTimes, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import Split from 'split.js';
import './OrderEditWorkspace.css';

const API = import.meta.env.VITE_API_URL;

// ----- localStorage helpers -----
const LS_KEYS = {
  vertical: 'oew-sizes-vertical',   // [top, bottom]
  horizontal: 'oew-sizes-horizontal'// [left, middle, right]
};
const readSizes = (key, expectedLen, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length !== expectedLen) return fallback;
    if (arr.some(v => typeof v !== 'number' || !isFinite(v) || v <= 0)) return fallback;
    const sum = arr.reduce((s, n) => s + n, 0);
    if (Math.abs(sum - 100) > 0.1) {
      return arr.map(v => (v / sum) * 100);
    }
    return arr;
  } catch {
    return fallback;
  }
};
const writeSizes = (key, sizes) => {
  try {
    localStorage.setItem(key, JSON.stringify(sizes));
  } catch {}
};

// ----- response helpers -----
function isJsonResponse(r) {
  const ct = r.headers.get('content-type') || '';
  return ct.includes('application/json');
}
async function safeJson(r) {
  if (!r) return null;
  if (r.status === 204) return null;
  if (isJsonResponse(r)) return r.json();
  await r.text();
  return null;
}

// --- money input helpers ---
const sanitizeMoney = (val) => {
  let s = String(val ?? '');
  // keep digits and dots only
  s = s.replace(/[^\d.]/g, '');
  // keep only the first dot
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  // limit to 2 decimals
  const parts = s.split('.');
  if (parts[1]) parts[1] = parts[1].slice(0, 2);
  s = parts.join('.');
  return s;
};
const normalizeMoney = (val) => {
  const s = sanitizeMoney(val);
  const n = Number(s);
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};

// ----- role helpers -----
function getUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}') || {}; }
  catch { return {}; }
}
function isClientUser(u) {
  const v = u?.isclient ?? u?.isClient ?? 0;
  if (typeof v === 'boolean') return v;
  return Number(v) === 1;
}

export default function OrderEditWorkspace({ order, editor }) {
  const orderId = Number(order?.id || order?.orderid || order?.ID);
  const IS_CLIENT = isClientUser(getUser());

  // Split.js DOM refs
  const topBlockRef = useRef(null);
  const bottomBlockRef = useRef(null);
  const leftRef = useRef(null);
  const midRef = useRef(null);
  const rightRef = useRef(null);
  const verticalInst = useRef(null);
  const horizontalInst = useRef(null);
  const splitInitedRef = useRef(false);

  // Data state
  const [charges, setCharges] = useState({ data: [], total: 0 });
  const [fees, setFees] = useState([]);
  const [items, setItems] = useState([]);
  // Bottom order items sort state (persisted)
  const ITEMS_SORT_FIELD_KEY = 'oew.items.sortField';
  const ITEMS_SORT_ORDER_KEY = 'oew.items.sortOrder';
  const normalizeItemsField = (field) => (field === 'totalunitsrequested' ? 'totalpiecesrequested' : field);
  const getInitItemsSortField = () => {
    try {
      const stored = localStorage.getItem(ITEMS_SORT_FIELD_KEY);
      return normalizeItemsField(stored || 'requestdate');
    } catch {
      return 'requestdate';
    }
  };
  const getInitItemsSortOrder = () => {
    try { return localStorage.getItem(ITEMS_SORT_ORDER_KEY) || 'DESC'; } catch { return 'DESC'; }
  };
  const [itemsSortField, setItemsSortField] = useState(getInitItemsSortField());
  const [itemsSortOrder, setItemsSortOrder] = useState(getInitItemsSortOrder());
  useEffect(() => { try { localStorage.setItem(ITEMS_SORT_FIELD_KEY, itemsSortField); } catch {} }, [itemsSortField]);
  useEffect(() => { try { localStorage.setItem(ITEMS_SORT_ORDER_KEY, itemsSortOrder); } catch {} }, [itemsSortOrder]);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  // custom charge inputs
  const [customName, setCustomName] = useState('');
  const [customQty, setCustomQty] = useState(1);
  const [customAmt, setCustomAmt] = useState('');
  // available charges qty map (fee.id -> qty)
  const [feeQty, setFeeQty] = useState({});

  // editing state for order charges
  const [editRowId, setEditRowId] = useState(null);
  const [editDraft, setEditDraft] = useState({ qty: 1, amt: 0, custom: '', mode: 'name' });

  const canLoad = useMemo(() => Number.isFinite(orderId) && orderId > 0, [orderId]);

  // Loaders
  const refreshCharges = useCallback(async () => {
    if (!canLoad || IS_CLIENT) return; // clients don't see charges
    try {
      const r = await fetch(`${API}/api/orders/${orderId}/charges`);
      if (!r.ok) throw new Error(`GET charges ${r.status}`);
      const json = await safeJson(r);
      setCharges({ data: json?.data || [], total: json?.total || 0 });
    } catch {
      setErrMsg('Failed to load charges.');
      setCharges({ data: [], total: 0 });
    }
  }, [canLoad, orderId, IS_CLIENT]);

  const refreshFees = useCallback(async () => {
    if (!canLoad || IS_CLIENT) return; // clients don't see available charges
    try {
      const r = await fetch(`${API}/api/orders/${orderId}/charges/available`);
      if (!r.ok) throw new Error(`GET fees ${r.status}`);
      const json = await safeJson(r);
      setFees(Array.isArray(json) ? json : []);
    } catch {
      setErrMsg('Failed to load available charges.');
      setFees([]);
    }
  }, [canLoad, orderId, IS_CLIENT]);

  const refreshItems = useCallback(async () => {
    if (!canLoad || IS_CLIENT) return; // clients don't see order items pane here
    try {
      const r = await fetch(`${API}/api/orders/${orderId}/items`);
      if (!r.ok) throw new Error(`GET items ${r.status}`);
      const json = await safeJson(r);
      setItems(Array.isArray(json) ? json : []);
    } catch {
      setErrMsg('Failed to load order items.');
      setItems([]);
    }
  }, [canLoad, orderId, IS_CLIENT]);

  const sortedItems = useMemo(() => {
    if (!Array.isArray(items) || !items.length) return [];
    const rows = [...items];
    const dir = itemsSortOrder === 'ASC' ? 1 : -1;
    rows.sort((a, b) => {
      const A = a[itemsSortField];
      const B = b[itemsSortField];
      const aVal = typeof A === 'number' ? A : (Date.parse(A) || (A ?? ''));
      const bVal = typeof B === 'number' ? B : (Date.parse(B) || (B ?? ''));
      if (aVal > bVal) return dir;
      if (aVal < bVal) return -dir;
      return 0;
    });
    return rows;
  }, [items, itemsSortField, itemsSortOrder]);

  // Totals for order line items
  const itemsTotals = useMemo(() => {
    try {
      const list = Array.isArray(items) ? items : [];
      const sum = (field) => list.reduce((acc, r) => acc + (Number(r?.[field]) || 0), 0);
      return {
        boxes_units: sum('boxes_units'),
        single_pieces: sum('single_pieces'),
        totalpiecesrequested: sum('totalpiecesrequested'),
        unitspicked: sum('unitspicked'),
        piecespicked: sum('piecespicked')
      };
    } catch {
      return { boxes_units: 0, single_pieces: 0, totalpiecesrequested: 0, unitspicked: 0, piecespicked: 0 };
    }
  }, [items]);

  const handleItemsSort = (field, e) => {
    if (!field) return;
    if (itemsSortField === field) setItemsSortOrder(itemsSortOrder === 'ASC' ? 'DESC' : 'ASC');
    else { setItemsSortField(field); setItemsSortOrder('ASC'); }
  };
  const renderItemsSortIcon = (field) => (
    itemsSortField !== field
      ? <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />
      : (itemsSortOrder === 'ASC'
        ? <FaSortUp style={{ color: 'var(--primary-color)', marginLeft: 4 }} />
        : <FaSortDown style={{ color: 'var(--primary-color)', marginLeft: 4 }} />)
  );

  // Initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!canLoad) return;
      setLoading(true);
      setErrMsg('');
      await Promise.all([refreshCharges(), refreshFees(), refreshItems()]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [canLoad, refreshCharges, refreshFees, refreshItems]);

  // Split.js setup with persistence (internal: 3 cols + bottom; client: editor only)
  useEffect(() => {
    if (splitInitedRef.current) return;
    if (!topBlockRef.current || !leftRef.current) return;

    const defaultVertical = [58, 42];
    const defaultHorizontal = [32, 34, 34];

    if (IS_CLIENT) {
      // Client: only editor pane, no splits required
      splitInitedRef.current = true;
      return () => {
        splitInitedRef.current = false;
      };
    }

    // Internal user: require all panes present
    if (!bottomBlockRef.current || !midRef.current || !rightRef.current) return;

    const vSizes = readSizes(LS_KEYS.vertical, 2, defaultVertical);
    const hSizes = readSizes(LS_KEYS.horizontal, 3, defaultHorizontal);

    const vertical = Split(
      [topBlockRef.current, bottomBlockRef.current],
      {
        direction: 'vertical',
        sizes: vSizes,
        minSize: [320, 220],
        gutterSize: 6,
        snapOffset: 0,
        onDragEnd: sizes => writeSizes(LS_KEYS.vertical, sizes),
      }
    );
    const horizontal = Split(
      [leftRef.current, midRef.current, rightRef.current],
      {
        sizes: hSizes,
        minSize: [260, 280, 260],
        gutterSize: 6,
        snapOffset: 0,
        onDragEnd: sizes => writeSizes(LS_KEYS.horizontal, sizes),
      }
    );

    verticalInst.current = vertical;
    horizontalInst.current = horizontal;
    splitInitedRef.current = true;

    const onResize = () => {
      try {
        const vs = vertical.getSizes();
        const hs = horizontal.getSizes();
        vertical.setSizes(vs);
        horizontal.setSizes(hs);
        writeSizes(LS_KEYS.vertical, vs);
        writeSizes(LS_KEYS.horizontal, hs);
      } catch {}
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      try { vertical.destroy(); } catch {}
      try { horizontal.destroy(); } catch {}
      verticalInst.current = null;
      horizontalInst.current = null;
      splitInitedRef.current = false;
    };
  }, [IS_CLIENT]);

  // Actions
  const removeCharge = async (id) => {
    try {
      await fetch(`${API}/api/orders/${orderId}/charges/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: 'system' })
      });
      refreshCharges();
    } catch {}
  };

  const addFeeCharge = async (fee, qty = 1) => {
    try {
      await fetch(`${API}/api/orders/${orderId}/charges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chargeid: fee.id,
          qty: Number(qty) || 1,
          amt: fee.amt,
          custom: fee.description || null,
          username: 'system'
        }),
      });
      refreshCharges();
    } catch {}
  };

  const addCustom = async () => {
    if (!customName || Number(customQty) <= 0) return;
    try {
      await fetch(`${API}/api/orders/${orderId}/charges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chargeid: 0,
          custom: customName,
          qty: Number(customQty) || 1,
          amt: Number(customAmt) || 0,
          username: 'system',
        }),
      });
      setCustomName(''); setCustomQty(1); setCustomAmt('');
      refreshCharges();
    } catch {}
  };

  const startEditCharge = (row) => {
    const isFeeRow = Number(row.chargeid) > 0;
    setEditRowId(row.id);
    setEditDraft({
      qty: Number(row.qty) || 1,
      amt: Number(row.amt) || 0,
      custom: isFeeRow ? (row.description || '') : (row.custom || row.chargename || ''),
      mode: isFeeRow ? 'description' : 'name',
    });
  };
  const cancelEditCharge = () => {
    setEditRowId(null);
    setEditDraft({ qty: 1, amt: 0, custom: '', mode: 'name' });
  };
  const saveEditCharge = async (id) => {
    try {
      const customPayload = (editDraft.custom ?? '').trim();
      const body = {
        qty: Number(editDraft.qty) || 1,
        amt: Number(editDraft.amt) || 0,
        custom: customPayload.length ? customPayload : null,
        username: 'system',
      };
      // Only send custom payload when editing description/name; otherwise omit to avoid overwriting
      if (editDraft.mode !== 'name' && editDraft.mode !== 'description') {
        delete body.custom;
      }
      await fetch(`${API}/api/orders/${orderId}/charges/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      cancelEditCharge();
      refreshCharges();
    } catch {
      setErrMsg('Failed to save charge changes.');
    }
  };

  if (!canLoad) {
    return (
      <div className="oew-root">
        <div className="oew-card"><b>No order id.</b></div>
      </div>
    );
  }

  return (
    <div className="oew-root">
      {/* TOP */}
      <div ref={topBlockRef} className="oew-vertical">
        {/* LEFT: editor */}
        <div ref={leftRef} className="oew-left oew-pane">
          <div className="oew-card">
            <div className="oew-card-head">
              {errMsg ? <span style={{color:'#b00020', fontSize:12}}>{errMsg}</span> : null}
            </div>
            <div className="oew-scroll">{editor}</div>
          </div>
        </div>

        {/* INTERNAL ONLY: order charges & available charges */}
        {!IS_CLIENT && (
          <>
            {/* MIDDLE: order charges */}
            <div ref={midRef} className="oew-middle oew-pane">
              <div className="oew-card darker">
                <div className="oew-card-head">
                  <h3 className="oew-title">Order Charges</h3>
                  <div className="oew-total">Total: ${Number(charges.total || 0).toFixed(2)}</div>
                </div>
                <div className="oew-scroll">
                  <table className="oew-table">
                    <thead>
                      <tr>
                        <th style={{width: 90}}></th>
                        <th>Qty</th>
                        <th>Charge</th>
                        <th>Amt</th>
                        <th>Row Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {charges.data?.length ? charges.data.map(row => {
                        const isEditing = editRowId === row.id;
                        const isFeeRow = Number(row.chargeid) > 0;
                        const qtyNum = isEditing ? Number(editDraft.qty) || 1 : Number(row.qty) || 0;
                        const amtNum = isEditing ? Number(editDraft.amt) || 0 : Number(row.amt) || 0;
                        const nameVal = row.chargename || (isFeeRow ? `Charge #${row.chargeid}` : 'Custom charge');
                        const descriptionVal = isFeeRow ? String(row.description || '').trim() : '';
                        const descriptionTooltip = descriptionVal.length ? descriptionVal : undefined;
                        const editingName = isEditing && editDraft.mode === 'name';
                        const editingDescription = isEditing && editDraft.mode === 'description';
                        const rowTotal = (qtyNum * amtNum);
                        return (
                          <tr key={row.id} onDoubleClick={() => startEditCharge(row)}>
                            <td>
                              {isEditing ? (
                                <>
                                  <button
                                    className="mini-btn"
                                    onClick={cancelEditCharge}
                                    title="Cancel edit"
                                  >
                                    <FaTimes />
                                  </button>
                                  <button
                                    className="mini-btn success solid"
                                    onClick={() => saveEditCharge(row.id)}
                                    title="Save changes"
                                    style={{ marginLeft: 6 }}
                                  >
                                    <FaSave />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className="mini-btn"
                                    onClick={() => startEditCharge(row)}
                                    title="Edit charge"
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    className="mini-btn danger"
                                    style={{ background: 'transparent', color: '#e74c3c', marginLeft: 6 }}
                                    onClick={() => removeCharge(row.id)}
                                    title="Remove charge"
                                  >
                                    <FaTimes />
                                  </button>
                                </>
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <input
                                  className="oew-input oew-narrow"
                                  type="number"
                                  min={1}
                                  value={editDraft.qty}
                                  onChange={(e) => setEditDraft(d => ({ ...d, qty: e.target.value }))}
                                  style={{ width: 64 }}
                                />
                              ) : qtyNum}
                            </td>
                            <td>
                              {editingName ? (
                                <input
                                  className="oew-input"
                                  value={editDraft.custom}
                                  onChange={(e) => setEditDraft(d => ({ ...d, custom: e.target.value }))}
                                  placeholder={nameVal || 'Charge name'}
                                  style={{ width: '100%' }}
                                />
                              ) : editingDescription ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <div>{nameVal}</div>
                                  <input
                                    className="oew-input"
                                    value={editDraft.custom}
                                    onChange={(e) => setEditDraft(d => ({ ...d, custom: e.target.value }))}
                                    placeholder="Description"
                                    style={{ width: '100%' }}
                                  />
                                </div>
                              ) : (
                                <span title={descriptionTooltip}>{nameVal}</span>
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <input
                                  className="oew-input oew-narrow"
                                  type="text"
                                  inputMode="decimal"
                                  pattern="^\\d+(\\.\\d{0,2})?$"
                                  value={String(editDraft.amt ?? '')}
                                  onChange={(e) => {
                                    const v = sanitizeMoney(e.target.value);
                                    setEditDraft(d => ({ ...d, amt: v }));
                                  }}
                                  onBlur={() => {
                                    setEditDraft(d => ({ ...d, amt: normalizeMoney(d.amt) }));
                                  }}
                                  style={{ width: 100 }}
                                  placeholder="0.00"
                                />
                              ) : `$${amtNum.toFixed(2)}`}
                            </td>
                            <td>{`$${rowTotal.toFixed(2)}`}</td>
                          </tr>
                        );
                      }) : <tr><td colSpan={5}>No charges yet.</td></tr>}
              </tbody>
              
            </table>
                </div>

                {/* REMARK: Custom charge fieldset temporarily hidden for future re-use */}
                {false && (
                  <fieldset className="oew-fieldset" aria-labelledby="oewCustomLegend">
                    <legend id="oewCustomLegend">Add Custom Charge</legend>
                    <div className="oew-custom-row">
                      <input className="oew-input" placeholder="Custom charge name" value={customName} onChange={e => setCustomName(e.target.value)} />
                      <div className="oew-field">
                        <label htmlFor="oewCustomQty" className="oew-label">Qty</label>
                        <input id="oewCustomQty" className="oew-input oew-narrow" type="number" value={customQty} onChange={e => setCustomQty(e.target.value)} min={1} />
                      </div>
                      <div className="oew-field">
                        <label htmlFor="oewCustomAmt" className="oew-label">Fee Amt</label>
                        <input
                          id="oewCustomAmt"
                          className="oew-input oew-narrow"
                          type="text"
                          inputMode="decimal"
                          pattern="^\\d+(\\.\\d{0,2})?$"
                          placeholder="0.00"
                          value={String(customAmt ?? '')}
                          onChange={(e) => setCustomAmt(sanitizeMoney(e.target.value))}
                          onBlur={() => setCustomAmt(a => normalizeMoney(a))}
                        />
                      </div>
                      <button className="btn primary" onClick={addCustom}>Add custom</button>
                    </div>
                  </fieldset>
                )}
              </div>
            </div>

            {/* RIGHT: available charges */}
            <div ref={rightRef} className="oew-right oew-pane">
            <div className="oew-card darker">
              <div className="oew-card-head">
                <h3 className="oew-title">Available Charges</h3>
              </div>
                <div className="oew-scroll">
                  <table className="oew-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th style={{width: 110}}>Default Rate</th>
                        <th style={{width: 72}}>Qty</th>
                        <th style={{width: 42}}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {fees.length ? fees.map(fee => {
                        const qtyVal = feeQty[fee.id] ?? 1;
                        const defaultAmt = Number(fee.amt || 0);
                        const defaultRateText = Number.isFinite(defaultAmt) ? `$${defaultAmt.toFixed(2)}` : '—';
                        const description = fee.description && fee.description.trim().length > 0
                          ? fee.description
                          : '—';
                        return (
                          <tr key={fee.id}>
                            <td>{fee.feename}</td>
                            <td>
                              <div>{description}</div>
                            </td>
                            <td>{defaultRateText}</td>
                            <td>
                              <input
                                className="oew-input oew-narrow"
                                type="number"
                                min={1}
                                value={qtyVal}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setFeeQty(prev => ({
                                  ...prev,
                                  [fee.id]: Math.max(1, Number(e.target.value) || 1)
                                }))}
                                style={{ width: '64px' }}
                              />
                            </td>
                            <td>
                              <button
                                className="mini-btn"
                                onClick={() => addFeeCharge(fee, qtyVal)}
                                title="Add charge to order"
                              >
                                +
                              </button>
                            </td>
                          </tr>
                        );
                      }) : <tr><td colSpan={5}>No available fees for this client/warehouse.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* INTERNAL ONLY: bottom order items */}
      {!IS_CLIENT && (
        <div ref={bottomBlockRef} className="oew-bottom">
          <div className="oew-card">
            <div className="oew-card-head"><h3 className="oew-title">Order Line Items (Order #{orderId})</h3></div>
            <div className="oew-scroll">
              <table className="oew-table orders-table">
                <thead>
                  <tr>
                    <th onClick={(e)=>handleItemsSort('statusname', e)} className={itemsSortField==='statusname' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='statusname'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='statusname'?'underline':undefined }}>Status {renderItemsSortIcon('statusname')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('requestdate', e)} className={itemsSortField==='requestdate' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='requestdate'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='requestdate'?'underline':undefined }}>Request Date {renderItemsSortIcon('requestdate')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('receiptid', e)} className={itemsSortField==='receiptid' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='receiptid'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='receiptid'?'underline':undefined }}>Receipt {renderItemsSortIcon('receiptid')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('palletnumber', e)} className={itemsSortField==='palletnumber' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='palletnumber'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='palletnumber'?'underline':undefined }}>Storage Unit # {renderItemsSortIcon('palletnumber')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('areaname', e)} className={itemsSortField==='areaname' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='areaname'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='areaname'?'underline':undefined }}>Area {renderItemsSortIcon('areaname')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('location', e)} className={itemsSortField==='location' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='location'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='location'?'underline':undefined }}>Location {renderItemsSortIcon('location')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('sku', e)} className={itemsSortField==='sku' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='sku'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='sku'?'underline':undefined }}>SKU {renderItemsSortIcon('sku')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('itemdescription', e)} className={itemsSortField==='itemdescription' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='itemdescription'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='itemdescription'?'underline':undefined }}>Item Description {renderItemsSortIcon('itemdescription')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('lotnum', e)} className={itemsSortField==='lotnum' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='lotnum'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='lotnum'?'underline':undefined }}>LOT {renderItemsSortIcon('lotnum')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('boxes_units', e)} className={itemsSortField==='boxes_units' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='boxes_units'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='boxes_units'?'underline':undefined }}>Boxes/Units {renderItemsSortIcon('boxes_units')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('single_pieces', e)} className={itemsSortField==='single_pieces' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='single_pieces'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='single_pieces'?'underline':undefined }}>Single Pieces {renderItemsSortIcon('single_pieces')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('totalpiecesrequested', e)} className={itemsSortField==='totalpiecesrequested' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='totalpiecesrequested'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='totalpiecesrequested'?'underline':undefined }}>Total Pieces Requested {renderItemsSortIcon('totalpiecesrequested')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('unitspicked', e)} className={itemsSortField==='unitspicked' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='unitspicked'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='unitspicked'?'underline':undefined }}>Units Picked {renderItemsSortIcon('unitspicked')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('piecespicked', e)} className={itemsSortField==='piecespicked' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='piecespicked'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='piecespicked'?'underline':undefined }}>Pieces Picked {renderItemsSortIcon('piecespicked')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('bol', e)} className={itemsSortField==='bol' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='bol'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='bol'?'underline':undefined }}>BOL {renderItemsSortIcon('bol')}</span>
                    </th>
                    <th onClick={(e)=>handleItemsSort('shipdate', e)} className={itemsSortField==='shipdate' ? 'active-sort' : ''} style={{ cursor:'pointer', userSelect:'none' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:4, color: itemsSortField==='shipdate'?'var(--primary-color)':undefined, textDecoration: itemsSortField==='shipdate'?'underline':undefined }}>Shipped Date {renderItemsSortIcon('shipdate')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.length ? sortedItems.map((r) => (
                    <tr key={r.id}>
                      <td>{r.statusname}</td>
                      <td>
                        {r.requestdate ? (
                          (() => {
                            const d = new Date(r.requestdate);
                            if (isNaN(d)) return String(r.requestdate);
                            const dateStr = d.toLocaleDateString();
                            const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            return (
                              <div className="oew-datetime">
                                <div>{dateStr}</div>
                                <div className="oew-time">{timeStr}</div>
                              </div>
                            );
                          })()
                        ) : ''}
                      </td>
                      <td>{r.receiptid}</td>
                      <td>{r.palletnumber}</td>
                      <td>{r.areaname}</td>
                      <td>{r.location}</td>
                      <td>{r.sku}</td>
                      <td>{r.itemdescription}</td>
                      <td>{r.lotnum}</td>
                      <td
                        className={(() => {
                          const remainingPieces = Number(r?.piecesonhand);
                          if (Number.isFinite(remainingPieces)) return remainingPieces <= 0 ? 'oi-all-inv' : '';
                          const remainingUnits = Number(r?.qtyunitsonhand);
                          if (Number.isFinite(remainingUnits)) return remainingUnits <= 0 ? 'oi-all-inv' : '';
                          return '';
                        })() || undefined}
                        title={(() => {
                          const remainingPieces = Number(r?.piecesonhand);
                          if (Number.isFinite(remainingPieces) && remainingPieces <= 0) return 'All inventory requested for this line item.';
                          const remainingUnits = Number(r?.qtyunitsonhand);
                          if (Number.isFinite(remainingUnits) && remainingUnits <= 0) return 'All inventory requested for this line item.';
                          return '';
                        })() || undefined}
                      >
                        {r.boxes_units}
                      </td>
                      <td>{r.single_pieces}</td>
                      <td>{r.totalpiecesrequested}</td>
                      <td>{r.unitspicked}</td>
                      <td>{r.piecespicked}</td>
                      <td>{r.bol}</td>
                      <td>{r.shipdate ? new Date(r.shipdate).toLocaleDateString() : ''}</td>
                    </tr>
                  )) : <tr><td colSpan={16}>No items on this order.</td></tr>}
                </tbody>
                <tfoot>
                  <tr>
                    <td>{sortedItems.length ? `Totals:(${Number(sortedItems.length).toLocaleString()} found)` : ''}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td style={{ fontWeight: 700, textAlign: 'right' }}></td>
                    <td></td>
                    <td style={{ fontWeight: 700 }}>{Number(itemsTotals.boxes_units || 0).toLocaleString()}</td>
                    <td style={{ fontWeight: 700 }}>{Number(itemsTotals.single_pieces || 0).toLocaleString()}</td>
                    <td style={{ fontWeight: 700 }}>{Number(itemsTotals.totalpiecesrequested || 0).toLocaleString()}</td>
                    <td style={{ fontWeight: 700 }}>{Number(itemsTotals.unitspicked || 0).toLocaleString()}</td>
                    <td style={{ fontWeight: 700 }}>{Number(itemsTotals.piecespicked || 0).toLocaleString()}</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {loading && <div style={{position:'absolute', inset:0, pointerEvents:'none'}} aria-hidden="true" />}
    </div>
  );
}
